import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import dayjs from '@/plugins/date'
import RestaurantSoftwareType from '@/enum/RestaurantSoftwareType'
import PeriodMapper from '@/services/mapper/PeriodMapper'
import {
    CIRCLE_DELIVERY_AREA_TYPE,
    circleDeliveryAreaName,
} from '@/services/Restaurants/DeliveryAreaName'
import { defaultCutoff } from '@/services/Orders/defaultCutoff'
import {
    TIME_FORMAT,
    DATE_FORMAT,
    DATE_TIME_FORMAT,
} from '@/const/DateTimeFormats'
import {
    personalPickupInformation,
    isPPINullable,
} from '@/services/Restaurants/PersonalPickupInformation'
import {
    applyPickupAddress,
    getPiClone,
    isPIfilled,
    isRestaurantPhone,
} from '@/services/Restaurants/PickupInformation'
import { normalizeDeliverySetting } from '@/services/Restaurants/DeliveryRules'

// Zone-level pre-order fields retired in tasks#166; never let them into a zone.
const PRE_ORDER_KEYS = [
    'isPreOrder',
    'preOrderBorderTime',
    'preOrderDayBefore',
    'preOrderUseHours',
    'preOrderHours',
]

// task #168: entity default of delivery_setting.preorder_prep_offset (both backends)
export const DEFAULT_PREORDER_PREP_OFFSET = 20

// a cleared input ('' / null / undefined) goes over the wire as an explicit null
const isBlank = (value) =>
    value === null || value === undefined || String(value).trim() === ''

const toIntOrNull = (value) => (isBlank(value) ? null : parseInt(value, 10))

// DECIMAL(4,1) per-km travel time: the API reads and writes one-decimal strings ("7.0")
const toOneDecimalOrNull = (value) =>
    isBlank(value) ? null : Number(value).toFixed(1)

class RestaurantFormMapper {
    toEntity(values) {
        const entity = cloneDeep(values)

        applyPickupAddress(entity)

        if (
            !Array.isArray(entity.acceptedPaymentMethodList) ||
            entity.acceptedPaymentMethodList.length === 0
        ) {
            entity.acceptedPaymentMethodList = ['stripe']
        }

        // convert to ids
        if (entity['coverImageMedia'] && entity['coverImageMedia']['@id']) {
            entity['coverImageMedia'] = entity['coverImageMedia']['@id']
        }

        if (entity['cardImageMedia'] && entity['cardImageMedia']['@id']) {
            entity['cardImageMedia'] = entity['cardImageMedia']['@id']
        }

        // opening days
        entity['openingDays'] = PeriodMapper.toEntity(entity['openingDays'])

        entity['specialClosures'].forEach((holiday) => {
            entity['openingDays'].push(holiday)
        })

        delete entity['specialClosures']

        entity.personalPickupInformation.minimumOrderValue = parseFloat(
            entity.personalPickupInformation.minimumOrderValue
        )
        if (
            entity.personalPickupInformation?.photoOfThePlace &&
            entity.personalPickupInformation?.photoOfThePlace['@id']
        ) {
            entity.personalPickupInformation.photoOfThePlace =
                entity.personalPickupInformation.photoOfThePlace['@id']
        }
        if (isPPINullable(entity.personalPickupInformation)) {
            entity.personalPickupInformation = null
        }

        // country settings
        // entity['country'] = store.getters[configType.getters.COUNTRY]

        // software settings
        if (!entity['softwareSettings']) {
            entity['softwareSettings'] = []
        }

        entity['softwareSettings'] = entity['softwareSettings'].filter(
            (softwareSetting) => {
                return softwareSetting.software !== RestaurantSoftwareType.ordit
            }
        )

        // the threshold goes over the wire as a decimal string (like RestaurantArea.minimumOrder);
        // the spread keeps the @id of an existing tier for the in-place update
        entity['deliverySetting'].preparationTimeTiers = (
            entity['deliverySetting'].preparationTimeTiers || []
        ).map((tier) => ({
            ...tier,
            minOrderValue: Number(tier.minOrderValue).toFixed(2),
            preparationTime: parseInt(tier.preparationTime, 10),
            eodLowBase: toIntOrNull(tier.eodLowBase),
        }))

        // task #168: the #155 future-days flag left the serializer groups (offset replaces it)
        delete entity['deliverySetting'].preparationTimeForFutureDays

        entity['deliverySetting'].defaultPreparationTime = toIntOrNull(
            entity['deliverySetting'].defaultPreparationTime
        )
        entity['deliverySetting'].defaultEodLowBase = toIntOrNull(
            entity['deliverySetting'].defaultEodLowBase
        )
        entity['deliverySetting'].travelTimeLowPerKm = toOneDecimalOrNull(
            entity['deliverySetting'].travelTimeLowPerKm
        )
        entity['deliverySetting'].travelTimeHighPerKm = toOneDecimalOrNull(
            entity['deliverySetting'].travelTimeHighPerKm
        )
        // null = "first slot at opening", a deliberate value
        entity['deliverySetting'].preorderPrepOffset = toIntOrNull(
            entity['deliverySetting'].preorderPrepOffset
        )

        // task #166: threshold as a decimal string, @id kept, the inactive mode's fields as null
        entity['deliverySetting'].preOrderTiers = (
            entity['deliverySetting'].preOrderTiers || []
        ).map((tier) => ({
            ...tier,
            minOrderValue: Number(tier.minOrderValue).toFixed(2),
            useHours: !!tier.useHours,
            borderTime: tier.useHours ? null : tier.borderTime,
            dayBefore: tier.useHours ? null : parseInt(tier.dayBefore, 10),
            hours: tier.useHours ? parseInt(tier.hours, 10) : null,
        }))

        // the offset's pre-filled default does not make a setting non-empty (a restaurant without
        // delivery keeps needing a delivery rule first, DeliverySetting.yml)
        if (
            !entity['deliverySetting'].deliveryRules.length &&
            !entity['deliverySetting'].preparationTimeTiers.length &&
            !entity['deliverySetting'].preOrderTiers.length &&
            typeof entity['deliverySetting'].defaultPreparationTime !==
                'number' &&
            entity['deliverySetting'].defaultEodLowBase === null &&
            entity['deliverySetting'].travelTimeLowPerKm === null &&
            entity['deliverySetting'].travelTimeHighPerKm === null &&
            entity['deliverySetting'].preorderPrepOffset ===
                DEFAULT_PREORDER_PREP_OFFSET
        ) {
            entity['deliverySetting'] = null
        }

        // a new setting without a default prep (e.g. only the offset was edited): the API setter takes an int only, so a
        // null would fail denormalisation with HTTP 500; leaving the key out lets the server answer the readable
        // validation error (a delivery rule is required) instead
        if (
            entity.deliverySetting &&
            !entity.deliverySetting['@id'] &&
            [null, undefined, ''].includes(
                entity.deliverySetting.defaultPreparationTime
            )
        ) {
            delete entity.deliverySetting.defaultPreparationTime
        }

        // the "Ordit manages delivery" switch is always on: "the restaurant delivers" is the
        // restaurant_managed method (tasks#211)
        if (entity.deliverySetting) {
            entity.deliverySetting.orditManagedDelivery = true
        }

        if (entity.deliverySetting?.deliveryRules?.length) {
            entity.deliverySetting.deliveryRules.forEach((rule) => {
                if (!rule.distanceTo) {
                    rule.distanceTo = null
                }
            })
        }

        if (!isPIfilled(entity.pickupInformation)) {
            entity.pickupInformation = null
        }

        if (entity['orditSoftwareSetting']) {
            entity['softwareSettings'].push(entity['orditSoftwareSetting'])
        }

        delete entity['orditSoftwareSetting']

        entity['softwareSettings'].forEach((softwareSetting) => {
            if (!softwareSetting || !softwareSetting['softwareLimits']) {
                return
            }

            softwareSetting['softwareLimits'].forEach((softwareLimit) => {
                softwareLimit['minOrder'] = parseFloat(
                    softwareLimit['minOrder']
                )
                softwareLimit['maxOrder'] = parseFloat(
                    softwareLimit['maxOrder']
                )
                softwareLimit['timeBeforeInMinute'] = parseInt(
                    softwareLimit['timeBeforeInMinute']
                )
            })
        })

        this.toEntityAreas(entity)

        if (entity.areas?.length) {
            entity.areas.forEach((restArea) => {
                if (restArea.area && restArea.area.length) {
                    restArea.area[0].push(restArea.area[0][0])
                }
            })
        }

        for (let field of [
            'cardTransactionFee',
            'commOnDelivery',
            'commOnFood',
            'restaurantCommOnFood',
            'restaurantCommOnDelivery',
            'restaurantCardTransactionFee',
        ]) {
            if (!isNaN(entity['billingInfo'][field])) {
                entity['billingInfo'][field] = (
                    entity['billingInfo'][field] / 100
                ).toString()
            }
        }

        entity['priceCategory'] = 'average'
        entity['isCatering'] = null
        entity['hiddenCompanies'] = []
        entity['preorderDayUntil'] = 30
        entity['billingInfo']['pickupAddress'] = entity['location']

        if (!entity.deliverooUrl) {
            entity.deliverooUrl = null
        }

        // int fiesta
        for (let field of [
            'fhrsId',
            'canteenSmallOrderLimit',
            'canteenSmallCutoffNotice',
            'canteenLargeCutoffNotice',
        ]) {
            entity[field] =
                entity[field] !== null && entity[field] !== ''
                    ? parseInt(entity[field])
                    : null
        }

        // null when empty
        for (let field of [
            'tabletSn',
            'preorderBorderTime',
            'canteenSmallEarliestPickup',
            'canteenSmallLatestPickup',
            'canteenSmallLatestCutoff',
            'canteenLargeEarliestPickup',
            'canteenLargeLatestPickup',
            'canteenLargeLatestCutoff',
            'thirdPartyType',
            'nonPartnerMenuUrl',
            'nonPartnerNotes',
            'internalName',
            'brand',
        ]) {
            if (!entity[field]) {
                entity[field] = null
            }
        }

        this.toEntityRules(entity)

        return entity
    }

    fromEntity(entity) {
        const formData = cloneDeep(entity)

        // openingDays
        const openingDays = []
        const specialClosures = []

        formData['openingDays'].forEach((value) => {
            if (value.date == null) {
                openingDays.push(value)
            } else {
                specialClosures.push(value)
            }
        })

        specialClosures.forEach((day) => {
            const intervals = []
            if (day.timeIntervals && Array.isArray(day.timeIntervals)) {
                day.timeIntervals.forEach((hour) => {
                    intervals.push({
                        openTime: PeriodMapper.getHour(hour.openTime),
                        closeTime: PeriodMapper.getHour(hour.closeTime),
                    })
                })
            }

            day.timeIntervals = intervals
            day.date = PeriodMapper.getDate(day.date)
        })

        formData['openingDays'] = openingDays
        formData['specialClosures'] = specialClosures

        formData['openingDays'] = PeriodMapper.fromEntity(
            formData['openingDays']
        )

        if (!formData.pickupInformation) {
            formData.pickupInformation = getPiClone()
        }
        // the courier phone field stays empty while couriers get the restaurant phone
        if (
            isRestaurantPhone(
                formData.pickupInformation.contactPhone,
                formData.phone
            )
        ) {
            formData.pickupInformation.contactPhone = ''
        }

        if (!formData['billingInfo']) {
            formData['billingInfo'] = {}
        }

        // the contact lists are edited as arrays; a saved null becomes [] here, not in the
        // component, so an untouched restaurant page is not dirty (tasks#211)
        ;['orderContacts', 'billingContacts'].forEach((key) => {
            if (!Array.isArray(formData.billingInfo[key])) {
                formData.billingInfo[key] = []
            }
        })
        if (!Array.isArray(formData.additionalApproveEmails)) {
            formData.additionalApproveEmails = []
        }

        if (!formData['personalPickupInformation']) {
            formData.personalPickupInformation = cloneDeep(
                personalPickupInformation
            )
        }

        if (!formData['deliverySetting']) {
            formData['deliverySetting'] = {
                defaultPreparationTime: null,
                preorderPrepOffset: DEFAULT_PREORDER_PREP_OFFSET,
            }
            if (!formData['deliverySetting']['deliveryRules']) {
                formData['deliverySetting']['deliveryRules'] = []
            }
        }

        // a switched-off setting shows (and saves) as "the restaurant delivers" (tasks#211)
        normalizeDeliverySetting(formData['deliverySetting'])

        if (
            !Array.isArray(formData['deliverySetting']['preparationTimeTiers'])
        ) {
            formData['deliverySetting']['preparationTimeTiers'] = []
        }

        formData['deliverySetting']['preparationTimeTiers'] = formData[
            'deliverySetting'
        ]['preparationTimeTiers'].map((tier) => ({
            ...tier,
            eodLowBase: tier.eodLowBase ?? null,
        }))

        // task #168: the API sends explicit nulls (no merge-patch on Restaurant); an absent key means
        // the field is not exposed, so a new setting starts from the entity default
        delete formData['deliverySetting']['preparationTimeForFutureDays']
        formData['deliverySetting']['defaultEodLowBase'] =
            formData['deliverySetting']['defaultEodLowBase'] ?? null
        formData['deliverySetting']['travelTimeLowPerKm'] =
            formData['deliverySetting']['travelTimeLowPerKm'] ?? null
        formData['deliverySetting']['travelTimeHighPerKm'] =
            formData['deliverySetting']['travelTimeHighPerKm'] ?? null
        if (formData['deliverySetting']['preorderPrepOffset'] === undefined) {
            formData['deliverySetting'][
                'preorderPrepOffset'
            ] = DEFAULT_PREORDER_PREP_OFFSET
        }

        if (!Array.isArray(formData['deliverySetting']['preOrderTiers'])) {
            formData['deliverySetting']['preOrderTiers'] = []
        }

        // the API sends a datetime for a TIME column; the picker works with HH:mm (as fromEntityAreas did)
        formData['deliverySetting']['preOrderTiers'] = formData[
            'deliverySetting'
        ]['preOrderTiers'].map((tier) => ({
            ...tier,
            useHours: !!tier.useHours,
            borderTime: tier.borderTime
                ? String(tier.borderTime).substr(11, 5)
                : null,
        }))

        if (!formData['timezoneRegion']) {
            formData['timezoneRegion'] = null
        }

        // time fields from datetime
        for (let field of [
            'preorderBorderTime',
            'canteenSmallEarliestPickup',
            'canteenSmallLatestPickup',
            'canteenSmallLatestCutoff',
            'canteenLargeEarliestPickup',
            'canteenLargeLatestPickup',
            'canteenLargeLatestCutoff',
        ]) {
            if (formData[field]) {
                formData[field] = formData[field].substr(11, 5)
            }
        }

        // translation obj
        if (
            !formData['translations'] ||
            Array.isArray(formData['translations'])
        ) {
            formData['translations'] = {
                en: {
                    badge: '',
                    banner: '',
                    description: '',
                    search: '',
                },
                hu: {
                    badge: '',
                    banner: '',
                    description: '',
                    search: '',
                },
            }
        }

        // software settings
        if (formData['softwareSettings']) {
            let orditSoftware = {}
            formData['softwareSettings'].forEach((softwareSetting) => {
                if (softwareSetting.software === RestaurantSoftwareType.ordit) {
                    orditSoftware = cloneDeep(softwareSetting)
                }
            })

            formData['orditSoftwareSetting'] = orditSoftware
        }

        formData.areas.forEach((restArea) => {
            if (restArea.area) {
                restArea.area[0].pop()
            }
        })

        this.fromEntityAreas(formData)

        for (let field of [
            'cardTransactionFee',
            'commOnDelivery',
            'commOnFood',
            'restaurantCommOnFood',
            'restaurantCommOnDelivery',
            'restaurantCardTransactionFee',
        ]) {
            if (formData['billingInfo'][field]) {
                formData['billingInfo'][field] = parseFloat(
                    (formData['billingInfo'][field] * 100).toFixed(2)
                )
            }
        }

        if (!formData['restaurantDiscount']) {
            formData['restaurantDiscount'] = {
                discountPriceEnabled: false,
                discountPriceOnlyForCanteen: false,
            }
        }

        this.fromEntityRules(formData, defaultCutoff)

        return formData
    }

    toEntityAreas(entity) {
        const areas = []
        const isCircle = entity.deliveryAreaType === CIRCLE_DELIVERY_AREA_TYPE
        const radiuses = entity.variantAreas.map(
            (variantArea) => variantArea.deliveryRadius
        )

        entity.variantAreas.forEach((variantArea) => {
            const name =
                (isCircle &&
                    circleDeliveryAreaName(
                        variantArea.deliveryRadius,
                        radiuses
                    )) ||
                variantArea.name
            // Areas are always active; only a running core-api
            // "inactivate for today" (inactivatedUntil) is kept.
            const isActive = variantArea.inactivatedUntil
                ? variantArea.isActive
                : true

            variantArea.variants.forEach((variant) => {
                const areaClone = cloneDeep(variantArea.area)
                const area = {
                    '@type': 'RestaurantArea',
                    area: areaClone,
                    deliveryDelay: 0,
                    deliveryPriceLimit: '0',
                    deliveryRadius: variantArea.deliveryRadius,
                    isActive,
                    name,
                    minimumOrder: String(variant.minimumOrder),
                    deliveryPrice: String(variant.deliveryPrice),
                    deliveryMinTime: variant.deliveryMinTime,
                    deliveryMaxTime: variant.deliveryMaxTime,
                }

                if (variant['@id']) {
                    area['@id'] = variant['@id']
                }

                areas.push(area)
            })
        })

        delete entity.variantAreas
        entity.areas = areas
    }

    fromEntityAreas(formData) {
        const areas = []

        formData.areas.forEach((modelArea) => {
            const copy = cloneDeep(modelArea)
            PRE_ORDER_KEYS.forEach((key) => delete copy[key])
            let found = null

            for (const area of areas) {
                if (
                    isEqual(area.area, copy.area) &&
                    area.deliveryRadius === copy.deliveryRadius &&
                    area.name === copy.name
                ) {
                    found = area
                    break
                }
            }

            const variant = {
                '@id': copy['@id'],
                minimumOrder: copy.minimumOrder,
                deliveryPrice: copy.deliveryPrice,
                deliveryMinTime: copy.deliveryMinTime,
                deliveryMaxTime: copy.deliveryMaxTime,
            }

            if (found) {
                found.variants.push(variant)
            } else {
                copy.variants = [variant]

                areas.push(copy)
            }
        })

        formData.variantAreas = areas
    }

    toEntityRules(entity) {
        if (!entity.defaultCanteenRules) return

        const defaultRules = entity.defaultCanteenRules.map((rule) => {
            const day = dayjs(rule.cutoffTime)
            return {
                cutoffTimeOverrideDay: dayjs(day.format(DATE_FORMAT)).diff(
                    defaultCutoff,
                    'd'
                ),
                cutoffTimeOverrideTime: day.format(TIME_FORMAT),
                orderLimit: rule.limit,
            }
        })
        entity.defaultCanteenRules = defaultRules
    }

    fromEntityRules(formData, cutoffTime) {
        formData.defaultCanteenRules = fromEntityRules(formData, cutoffTime)
    }
}

export function fromEntityRules(formData, cutoffTime) {
    if (!formData?.defaultCanteenRules) return

    const hourOffset = dayjs(cutoffTime).hour() - dayjs(defaultCutoff).hour()
    const minuteOffset =
        dayjs(cutoffTime).minute() - dayjs(defaultCutoff).minute()

    const defaultRules = formData.defaultCanteenRules.map((rule) => {
        const sameDay = rule.cutoffTimeOverrideDay === 0
        const time = dayjs(rule.cutoffTimeOverrideTime)
        let cutoff = dayjs(cutoffTime)
            .add(rule.cutoffTimeOverrideDay, 'd')
            .hour(time.hour() + (sameDay ? hourOffset : 0))
            .minute(time.minute() + (sameDay ? minuteOffset : 0))

        return {
            cutoffTime: cutoff.format(DATE_TIME_FORMAT),
            limit: rule.orderLimit,
        }
    })
    return defaultRules
}

export default new RestaurantFormMapper()
